// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface Provider {
  id?: string;
  name: string;
  code: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  email: string; // Required: Email for LOA notifications
  contactName?: string; // Optional: Primary contact person name
  isActive?: boolean;
  // Address fields (ISO 19160 compliant) - all optional for providers
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  townCity?: string;
  stateProvinceCounty?: string;
  postalCode?: string;
  countryCode?: string;
  country?: string;
  addressMetadata?: Record<string, unknown>;
  // Provider Portal fields
  portalSubdomain?: string;
  portalEnabled?: boolean;
  portalEnabledAt?: string;
  portalEnabledBy?: string;
  keycloakRealmId?: string;
  portalClientId?: string;
  portalClientSecret?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const API_BASE = '/api/providers';

export const providerApi = {
  async getAll(params?: {
    search?: string;
    isActive?: boolean;
    page?: number;
    size?: number;
  }): Promise<PageResponse<Provider>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', String(params.isActive));
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch providers');
    }
    const data = await response.json();
    // Return full paginated response
    return data;
  },

  async getById(id: string): Promise<Provider> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch provider');
    }
    return response.json();
  },

  async create(provider: Provider): Promise<Provider> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(provider)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to create provider');
    }
    return response.json();
  },

  async update(id: string, provider: Provider): Promise<Provider> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(provider)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update provider');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete provider');
    }
  },

  async enablePortal(id: string, portalSubdomain: string): Promise<EnablePortalResponse> {
    const response = await fetch(`${API_BASE}/${id}/enable-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ portalSubdomain })
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to enable provider portal');
    }
    return response.json();
  },

  async disablePortal(id: string): Promise<Provider> {
    const response = await fetch(`${API_BASE}/${id}/disable-portal`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to disable provider portal');
    }
    return response.json();
  }
};

export interface EnablePortalResponse {
  portalSubdomain: string;
  keycloakRealmId: string;
  portalClientId: string;
  portalEnabled: boolean;
  portalEnabledAt: string;
  portalEnabledBy: string;
  portalUrl: string;
}
